import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { getScrollTop, scrollTo as restoreScroll } from '@theme/scroll-container';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {boolean} */
  #apDrawerScrollLocked = false;

  /** @type {number} */
  #apDrawerScrollY = 0;

  /** @type {boolean} */
  #apDrawerFixedBody = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#prepareApMegaDrawer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#unlockApDrawerPage();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    if (this.classList.contains('ap-mega-drawer')) {
      this.#prepareApMegaDrawer();
    }

    const details = this.#getDetailsElement(event);

    if (this.classList.contains('ap-mega-drawer') && details === this.refs.details) {
      this.#lockApDrawerPage();
    }

    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // AP customization: unlock before the slide finishes so homepage carousels
    // are not stuck behind touchmove / overflow:hidden (ticket 38262).
    if (this.classList.contains('ap-mega-drawer') && details === this.refs.details) {
      this.#unlockApDrawerPage();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
    let closed = false;
    const finishClose = () => {
      if (closed) return;
      closed = true;
      reset(details);
      if (details === this.refs.details) {
        this.#unlockApDrawerPage();
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        for (const openDetail of openDetails) {
          if (openDetail instanceof HTMLDetailsElement) reset(openDetail);
        }
      } else {
        trapFocus(this.refs.details);
      }
    };

    onAnimationEnd(drawer || details, finishClose, { subtree: false });
    if (this.classList.contains('ap-mega-drawer')) {
      setTimeout(finishClose, 400);
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    });
  }

  /**
   * iOS-safe page lock while the AP drawer is open (ticket 38262).
   * Horizon `lockScroll` only sets overflow hidden, which iOS Safari ignores.
   * Save scroll and pin the body *before* overflow:hidden or the page jumps
   * to top (html scroll-behavior: smooth then eases back, logo remorphs).
   */
  #lockApDrawerPage() {
    if (this.#apDrawerScrollLocked) return;

    this.#apDrawerScrollLocked = true;
    this.#apDrawerScrollY = getScrollTop();

    this.#apDrawerFixedBody = true;
    document.body.style.setProperty('position', 'fixed');
    document.body.style.setProperty('top', `-${this.#apDrawerScrollY}px`);
    document.body.style.setProperty('left', '0');
    document.body.style.setProperty('right', '0');
    document.body.style.setProperty('width', '100%');

    document.body.classList.add('ap-mega-open');
    this.closest('header-component')?.setAttribute('data-ap-mega-open', '');
    document.getElementById('header-group')?.setAttribute('data-ap-mega-open', '');
    document.documentElement.classList.add('ap-mega-scroll-lock');
    document.querySelector('.page-wrapper')?.classList.add('ap-mega-scroll-lock');

    document.addEventListener('touchmove', this.#onApDrawerTouchMove, { passive: false });
    this.#applyApMegaHeaderColor();
  }

  /**
   * Match desktop overlay: copy menu text color onto the header (ticket 38262).
   */
  #applyApMegaHeaderColor() {
    const header = this.closest('header-component');
    const menu = document.querySelector('header-menu.ap-mega-menu');
    if (!(header instanceof HTMLElement) || !(menu instanceof HTMLElement)) return;

    const color = menu.style.getPropertyValue('--ap-mega-header-color').trim();
    const rgb = menu.style.getPropertyValue('--ap-mega-header-color-rgb').trim();
    if (!color) return;

    header.style.setProperty('--ap-mega-header-color', color);
    if (rgb) header.style.setProperty('--ap-mega-header-color-rgb', rgb);
  }

  #clearApMegaHeaderColor() {
    const header = this.closest('header-component');
    if (!(header instanceof HTMLElement)) return;

    header.style.removeProperty('--ap-mega-header-color');
    header.style.removeProperty('--ap-mega-header-color-rgb');
  }

  /**
   * @param {TouchEvent} event
   */
  #onApDrawerTouchMove = (event) => {
    const drawer = this.refs.menuDrawer;
    if (!drawer || event.touches.length > 1) return;

    const target = event.target;
    if (!(target instanceof Node) || !drawer.contains(target)) {
      event.preventDefault();
    }
  };

  #unlockApDrawerPage() {
    if (!this.#apDrawerScrollLocked) return;

    document.removeEventListener('touchmove', this.#onApDrawerTouchMove);
    document.body.classList.remove('ap-mega-open');
    this.closest('header-component')?.removeAttribute('data-ap-mega-open');
    document.getElementById('header-group')?.removeAttribute('data-ap-mega-open');
    document.documentElement.classList.remove('ap-mega-scroll-lock');
    document.querySelector('.page-wrapper')?.classList.remove('ap-mega-scroll-lock');

    if (this.#apDrawerFixedBody) {
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('left');
      document.body.style.removeProperty('right');
      document.body.style.removeProperty('width');
      restoreScroll({ top: this.#apDrawerScrollY, behavior: 'instant' });
      this.#apDrawerFixedBody = false;
    }

    this.#apDrawerScrollLocked = false;
    this.#clearApMegaHeaderColor();
  }

  /**
   * Copy overlay panels into L2, then turn empty items back into links (ticket 38262).
   * Skip until `header-menu.ap-mega-menu` exists so we never convert Over ons to a
   * link before we can see its featured cards.
   */
  #prepareApMegaDrawer() {
    if (!this.classList.contains('ap-mega-drawer')) return;

    const sourceMenu = document.querySelector('header-menu.ap-mega-menu');
    if (!(sourceMenu instanceof HTMLElement)) return;

    this.#hydrateApPanels();
    this.#syncApMegaDrawerItems(sourceMenu);
  }

  /**
   * Clone desktop mega panels into the drawer (content_for can only run once) (ticket 38262).
   */
  #hydrateApPanels() {
    if (this.hasAttribute('data-ap-mega-hydrated')) return;

    const sourceMenu = document.querySelector('header-menu.ap-mega-menu');
    if (!sourceMenu) return;

    const slots = this.querySelectorAll('.ap-mega-drawer__panel-slot');
    for (const slot of slots) {
      if (!(slot instanceof HTMLElement)) continue;

      const title = this.#normalizeApLabel(slot.getAttribute('data-ap-mega-item'));
      const handle = this.#normalizeApLabel(slot.getAttribute('data-ap-mega-handle'));
      const panels = sourceMenu.querySelectorAll('.ap-mega-panel');
      let match = null;

      for (const panel of panels) {
        if (!(panel instanceof HTMLElement)) continue;

        const itemTitle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-item'));
        const itemHandle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-handle'));

        if (itemHandle && handle && itemHandle === handle) {
          match = panel;
          break;
        }
        if (!match && itemTitle && itemTitle === title) match = panel;
      }

      if (!match) continue;

      const clone = /** @type {HTMLElement} */ (match.cloneNode(true));
      clone.classList.add('is-active');
      clone.classList.add('ap-mega-panel--stacked');
      clone.removeAttribute('hidden');
      this.#layoutApMegaPanel(clone);

      // Paint the whole L2 submenu with the panel's customizer background, not only the panel (ticket 2323).
      const submenu = slot.closest('.ap-mega-drawer__submenu');
      const panelBg = match.style.getPropertyValue('--ap-mega-panel-bg').trim();
      if (submenu instanceof HTMLElement && panelBg) {
        submenu.style.setProperty('--ap-mega-drawer-submenu-bg', panelBg);
      }

      const editorGroups = clone.querySelector('.ap-mega-panel__editor-groups');
      const hasEditorGroups = Boolean(editorGroups && editorGroups.childElementCount > 0);
      const cloneHasNav = Boolean(
        clone.querySelector('.ap-mega-panel__links .ap-mega-group, .ap-mega-panel__editor-groups .ap-mega-group')
      );
      const slotGroups = slot.querySelector('.ap-mega-groups');

      // Keep Liquid nav groups when the desktop clone has none (parent_link miss).
      // Always mount a `.ap-mega-panel` so link color / stacked gap / featured layout apply.
      if (slotGroups && !cloneHasNav && !hasEditorGroups) {
        const linksCol = clone.querySelector('.ap-mega-panel__links');
        if (linksCol) {
          linksCol.prepend(slotGroups);
        }
      }

      for (const groups of clone.querySelectorAll('.ap-mega-groups')) {
        groups.classList.add('ap-mega-groups--stacked');
      }

      slot.replaceChildren(clone);
    }

    this.setAttribute('data-ap-mega-hydrated', '');
  }

  /**
   * Featured-only / cards panels have no Shopify children, so Liquid always
   * emits L2. Convert back to a normal link when the live overlay panel is empty.
   * @param {HTMLElement} sourceMenu
   */
  #syncApMegaDrawerItems(sourceMenu) {
    const items = this.querySelectorAll('.ap-mega-drawer__item');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      const details = item.querySelector(':scope > details');
      if (!(details instanceof HTMLDetailsElement)) continue;
      if (this.#drawerItemHasMegaContent(details, sourceMenu)) continue;

      this.#replaceApMegaDetailsWithLink(details);
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} sourceMenu
   * @returns {boolean}
   */
  #drawerItemHasMegaContent(details, sourceMenu) {
    const slot = details.querySelector('.ap-mega-drawer__panel-slot');
    const title = this.#normalizeApLabel(slot?.getAttribute('data-ap-mega-item'));
    const handle = this.#normalizeApLabel(slot?.getAttribute('data-ap-mega-handle'));
    const sourcePanel = this.#findSourceApPanel(sourceMenu, title, handle);

    if (sourcePanel && this.#apPanelHasContent(sourcePanel)) return true;

    const cloned = slot?.querySelector(':scope > .ap-mega-panel');
    if (cloned instanceof HTMLElement && this.#apPanelHasContent(cloned)) return true;

    return Boolean(slot?.querySelector('.ap-mega-group, .ap-mega-card'));
  }

  /**
   * @param {HTMLElement} sourceMenu
   * @param {string} title
   * @param {string} handle
   * @returns {HTMLElement | null}
   */
  #findSourceApPanel(sourceMenu, title, handle) {
    const panels = sourceMenu.querySelectorAll('.ap-mega-panel');

    let titleMatch = null;

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;

      const itemTitle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-item'));
      const itemHandle = this.#normalizeApLabel(panel.getAttribute('data-ap-mega-handle'));

      if (itemHandle && handle && itemHandle === handle) return panel;
      if (!titleMatch && itemTitle && itemTitle === title) titleMatch = panel;
    }

    return titleMatch;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  #apPanelHasContent(panel) {
    const flagged = panel.getAttribute('data-ap-mega-has-content');
    if (flagged === 'false') return false;
    if (flagged === 'true') return true;

    return Boolean(
      panel.querySelector('.ap-mega-group, .ap-mega-card, .ap-mega-featured-card, .ap-mega-link-group')
    );
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #replaceApMegaDetailsWithLink(details) {
    const url = details.getAttribute('data-ap-mega-url') || '#';
    const summary = details.querySelector('summary');
    const title = summary?.querySelector('.menu-drawer__menu-item-text')?.textContent ?? '';
    const id = summary?.id ?? '';
    const isActive = Boolean(summary?.classList.contains('menu-drawer__menu-item--active'));

    const link = document.createElement('a');
    link.href = url;
    if (id) link.id = id;
    link.className =
      'menu-drawer__menu-item menu-drawer__menu-item--mainlist menu-drawer__animated-element ap-mega-drawer__summary';
    if (isActive) {
      link.classList.add('menu-drawer__menu-item--active');
      link.setAttribute('aria-current', 'page');
    }

    const span = document.createElement('span');
    span.className = 'menu-drawer__menu-item-text wrap-text';
    span.textContent = title;
    link.append(span);

    details.replaceWith(link);
    link.addEventListener('animationend', removeWillChangeOnAnimationEnd);
  }

  /**
   * @param {string | null | undefined} value
   * @returns {string}
   */
  #normalizeApLabel(value) {
    return (value ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /**
   * Featured-card `logo` is the live setting (nested stubs are empty). Clone it
   * as a panel sibling before the featured column (Figma 208:11113).
   * @param {HTMLElement} panel
   */
  #placeApMegaLogo(panel) {
    const links = panel.querySelector('.ap-mega-panel__links');
    if (!links || panel.querySelector(':scope > .ap-mega-panel__logo')) return;

    const donor = panel.querySelector('.ap-mega-panel-logo-donor');
    if (!(donor instanceof HTMLElement)) return;

    const wrap = /** @type {HTMLElement} */ (donor.cloneNode(true));
    wrap.classList.remove('ap-mega-panel-logo-donor');
    wrap.classList.add('ap-mega-panel__logo');

    const featured = panel.querySelector('.ap-mega-panel__featured');
    if (featured) {
      panel.insertBefore(wrap, featured);
    } else {
      panel.append(wrap);
    }

    panel.classList.add('ap-mega-panel--has-logo');
  }

  /**
   * @param {HTMLElement} panel
   */
  #layoutApMegaPanel(panel) {
    this.#placeApMegaLogo(panel);

    const featured = panel.querySelector('.ap-mega-panel__featured');
    const groupsSlot = panel.querySelector('.ap-mega-panel__editor-groups');
    if (!featured || !groupsSlot) return;

    const groups = featured.querySelectorAll('.ap-mega-group');
    for (const group of groups) {
      let node = group;
      while (node.parentElement && node.parentElement !== featured) {
        node = node.parentElement;
      }
      groupsSlot.append(node);
    }

    const shopifyGroups = panel.querySelector('.ap-mega-panel__links > .ap-mega-groups');
    if (shopifyGroups instanceof HTMLElement) {
      shopifyGroups.hidden = groupsSlot.childElementCount > 0;
    }
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
